import { bundleActions } from '@/actions';
import { promiseWrapper } from '@/helpers';
import { bundleCheckoutTrackingManager } from '@/helpers/checkout';
import { buyBundleFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import useWindowOnUnload from '@/hooks/useWindowOnUnload';
import { userService } from '@/services';
import { ClientBundle, clientBundleSchema } from '@/types/api/services/users/schema';
import { SelectedPaymentMethod } from '@/types/checkout';
import { Dispatch, useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';

type BundleConfirmationState = {
  bundle: ClientBundle | null;
  loading: boolean;
  error: boolean;
};

type BundleConfirmationAction =
  | {
      type: 'SET_BUNDLE';
      payload: ClientBundle;
    }
  | {
      type: 'SET_LOADING';
    }
  | {
      type: 'SET_ERROR';
    };

async function fetchAndSetBundle(bundleId: number, dispatch: Dispatch<BundleConfirmationAction>) {
  dispatch({ type: 'SET_LOADING' });

  const { data, error } = await promiseWrapper(userService.getBundleById(bundleId));

  const validateResponse = clientBundleSchema.safeParse(data);

  if (error || !validateResponse.success) {
    dispatch({ type: 'SET_ERROR' });
    return;
  }

  dispatch({ type: 'SET_BUNDLE', payload: validateResponse.data });
}

function bundleConfirmationReducer(
  state: BundleConfirmationState,
  action: BundleConfirmationAction,
): BundleConfirmationState {
  switch (action.type) {
    case 'SET_BUNDLE': {
      return {
        ...state,
        bundle: action.payload,
        loading: false,
      };
    }
    case 'SET_LOADING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'SET_ERROR': {
      return {
        ...state,
        error: true,
        loading: false,
      };
    }
    default:
      return state;
  }
}

const useBundleConfirmation = () => {
  const location = useLocation<{ selectedPaymentMethod?: SelectedPaymentMethod }>();
  const selectedPaymentMethod = location.state?.selectedPaymentMethod;
  const query = new URLSearchParams(location.search);
  const bundleId = query.get('bundleId') || '';

  const [state, dispatch] = useReducer(bundleConfirmationReducer, {
    bundle: null,
    loading: true,
    error: false,
  });

  useEffect(() => {
    if (!bundleId) return;
    fetchAndSetBundle(+bundleId, dispatch);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state.bundle) return;
    bundleCheckoutTrackingManager().clearTrackingProps();

    buyBundleFlowTrackingSession.checkoutConfirmation({ type: 'bundle', data: state.bundle });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.bundle]);

  useWindowOnUnload({
    fn: () => {
      buyBundleFlowTrackingSession.clearTrackingSession();
      bundleActions.clearBundleCheckout();
    },
    callOnCleanup: true,
  });

  return {
    bundle: state.bundle,
    loading: state.loading,
    error: state.error,
    selectedPaymentMethod,
  };
};

export default useBundleConfirmation;
